import { v2UsersLoginSocialGoogleCreate } from '@on3/api';
import { SocialIcon } from '@on3/ui-lib/components/SocialIcon';
import { externalApi, useAuth } from '@on3/ui-lib/index';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

declare global {
  interface Window {
    google: any;
  }
}

const loadGoogle = () =>
  new Promise((res, rej) => {
    const script = document.createElement('script');

    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;

    script.onload = () => res(true);
    script.onerror = () => rej(new Error('Google API script failed to load'));

    document.body.appendChild(script);
  });

const signInUser = async (idToken: string) => {
  const user = await v2UsersLoginSocialGoogleCreate(externalApi, {
    idToken,
    registerIfNew: true,
  });

  return user;
};

export const GoogleButton = () => {
  const { setUserToken } = useAuth();

  const params = useSearchParams();
  const returnTo = params.get('returnTo') || '/';

  const router = useRouter();

  const [scriptLoaded, setScriptLoaded] = useState(false);

  useEffect(() => {
    loadGoogle().then(() => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '',
          scope: 'email openid profile',
          callback: async (response: { credential: string }) => {
            try {
              const user = await signInUser(response.credential);

              window.top?.postMessage({
                type: 'USER_LOGIN',
                jwt: user,
              });

              setUserToken(user);
              router.push(returnTo);
            } catch (error) {
              console.error('Error signing in user:', error);
            }
          },
        });

        setScriptLoaded(true);
      }
    });
  }, [router, returnTo, setUserToken]);

  const handleGoogleAuth = useCallback(() => {
    if (scriptLoaded) {
      window.google.accounts.id.prompt();
    }
  }, [scriptLoaded]);

  return (
    <button disabled={!scriptLoaded} onClick={handleGoogleAuth} type="button">
      <SocialIcon type="google" />
      <span className="sr-only">Sign in with Google</span>
    </button>
  );
};
